import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createElementBlock as _createElementBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = (n: any) => (_pushScopeId("data-v-79f26ee2"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "installed-detail-root" }
const _hoisted_2 = { class: "table-criteria" }
const _hoisted_3 = { class: "left-bar" }
const _hoisted_4 = { class: "right-bar" }

import { computed, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import { ElMessage } from 'element-plus'
import dayjs from 'dayjs'
import CmTable from '@/components/cmTable/index.vue'
import type { TableOpt, TableActOpt } from '@/components/cmTable/types'
import type { PageOption } from '@/components/cmTable/types'
import { FormSize } from '@/components/cmForm/types'
import { useCmTableListRefresh } from '@/hooks/use-cm-table-list-refresh'
import { useParseTime } from '@/hooks'
import ExportDropdown from '@/views/dashboard/_components/export-dropdown/export-dropdown.vue'
import { SoftwareInfo, InstalledDetailService, QueryParams } from './installed-detail.service'
import { TableDataRowState } from '@/services/table-memory-pagination'
import { formatTableEmpty } from '@/utils/format'
import { formatDuration, formatTime, formatTimeStamp } from '@/utils/time'
import { splitSoftHashKey } from '@/utils/tool'

export default /*#__PURE__*/_defineComponent({
  __name: 'installed-detail',
  props: {
    groupId: { default: null },
    deviceId: { default: '' },
    computerName: { default: '' },
    dateRange: { default: () => [] },
    hasPermission: { type: Boolean, default: true }
  },
  setup(__props: any) {

const props = __props

const hasPermission = computed(() => props.hasPermission)

const { t, locale } = useI18n()
const injector = useReflectiveInjector([InstalledDetailService])
const service = injector.get(InstalledDetailService)

const tableRef = ref<{ getList: () => Promise<unknown> } | null>(null)
const { refreshList } = useCmTableListRefresh(tableRef)

function rangeToStartEnd() {
  const r =
    props.dateRange?.length === 2
      ? props.dateRange
      : [dayjs().subtract(3, 'month').format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')]
  const [s, e] = useParseTime(r)
  return {
    start_time: s != null ? String(s) : undefined,
    end_time: e != null ? String(e) : undefined,
  }
}

const se = rangeToStartEnd()

const listForm = reactive<QueryParams>({
  limit: 20,
  page: 1,
  useMemory: false,
  device_id: props.deviceId,
  start_time: se.start_time,
  end_time: se.end_time,
  soft_name: '',
  asc: false,
  sort: 'avg_use_count',
})

const viewData = ref({
  tableData: [] as TableDataRowState<SoftwareInfo>[],
  total: 0,
})

const uninstallDialogVisible = ref(false)
const uninstallType = ref<0 | 1>(0)
const pendingUninstallRow = ref<TableDataRowState<SoftwareInfo> | null>(null)

const pageOption = reactive<PageOption>({
  style: 'padding: 12px 0',
  pageNumName: 'page',
  pageSizeName: 'limit',
  pageSizes: [10, 20, 30, 50, 100],
})

function pullServiceState() {
  const state = service.getViewState()
  viewData.value.tableData = state.data.list
  viewData.value.total = state.data.total
}

function onSoftNameChange() {
  listForm.page = 1
  listForm.useMemory = false
  void refreshList()
}

function formatLastActive(row: SoftwareInfo) {
  const v = row.last_active_time
  if (v == null || v === '') return '--'
  return formatTimeStamp(String(v), 'YYYY-MM-DD HH:mm:ss')
}

async function listApi(params: Record<string, unknown>) {
  if (!props.deviceId) {
    return { list: [] as TableDataRowState<SoftwareInfo>[], total: 0 }
  }
  listForm.page = Number(params.page) || 1
  listForm.limit = Number(params.limit) || listForm.limit
  const sortKey = params.sort as string | undefined
  if (sortKey != null && sortKey !== '') {
    listForm.sort = sortKey
    listForm.asc = Boolean(params.asc)
  }
  await service.refresh({
    ...listForm,
    device_id: props.deviceId,
  })
  pullServiceState()
  return {
    list: viewData.value.tableData,
    total: viewData.value.total,
  }
}

const tableActOpt = computed<TableActOpt>(() => {
  void locale.value
  return {
    columnOpt: {
      label: t('listColumns.operation'),
      width: 100,
      align: 'left',
      fixed: 'right',
    },
    actionButtons: () => [
      {
        name: t('genuineSoftwareUsage.uninstall'),
        type: 'uninstall',
        opt: { size: FormSize.Small, text: true, type: 'text' },
      },
    ],
  }
})

const tableOpt = computed((): TableOpt => {
  void locale.value
  return {
    selection: false,
    rowKey: 'soft_hash_key',
    path: 'list',
    totalPath: 'total',
    height: '100%',
    usePagination: false,
    disableScrollLoad: true,
    showTip: false,
    bodyStyle: 'height: 100%; padding: 0',
    containerOpt: { style: { flex: '1', minHeight: 0, overflow: 'hidden' } },
    columns: [
      {
        prop: 'soft_name',
        label: t('softwareDispatcher.softName'),
        columnOpt: { minWidth: 120 },
      },
      {
        prop: 'soft_version',
        label: t('softwareDispatcher.softVersion'),
        columnOpt: { minWidth: 130 },
        formatter: (row: SoftwareInfo, value: unknown, column: unknown) =>
          formatTableEmpty(row, column, value),
      },
      {
        prop: 'avg_duration',
        label: t('softwareUsage.avgDuration'),
        minWidth: 130,
        columnOpt: { sortable: 'custom' },
      },
      {
        prop: 'avg_use_count',
        label: t('softwareUsage.avgUseCount'),
        minWidth: 130,
        columnOpt: { sortable: 'custom' },
      },
      {
        prop: 'last_active_time',
        label: t('softwareUsage.lastActiveTime'),
        minWidth: 170,
        columnOpt: { sortable: 'custom' },
      },
      {
        prop: 'install_time',
        label: t('softwareUsage.latestInstallTime'),
        minWidth: 170,
        columnOpt: { sortable: 'custom' },
      },
    ],
  }
})

function download(type: 0 | 1) {
  service.download(
    type,
    { ...listForm, device_id: props.deviceId },
    t('softwareUsage.manageExportPrefixTerminalView') +
      t('genuineSoftwareUsage.exportTerminalUsedSoft', { name: props.computerName })
  )
}

function handleTableAction({
  type,
  rowItem,
}: {
  type: string
  rowItem?: TableDataRowState<SoftwareInfo>
}) {
  if (type === 'uninstall' && rowItem) {
    pendingUninstallRow.value = rowItem
    uninstallType.value = 0
    uninstallDialogVisible.value = true
  }
}

function resetUninstallDialog() {
  uninstallType.value = 0
  pendingUninstallRow.value = null
}

function confirmUninstall() {
  const row = pendingUninstallRow.value
  if (!row?.soft_hash_key) {
    uninstallDialogVisible.value = false
    return
  }
  uninstallDialogVisible.value = false
  const softHashKey = splitSoftHashKey(row.soft_hash_key)
  service
    .uninstallSoftware({
      uninstall_type: uninstallType.value,
      device_id: props.deviceId,
      soft_hash_keys: [...softHashKey],
    })
    .then(() => {
      ElMessage.success(t('genuineSoftwareUsage.uninstallSuccessTip', { count: 1 }))
      void refreshList()
    })
    .catch(() => {
      ElMessage.error(t('softwareUsage.uninstallClientTerminalSoftFail', { count: 1 }))
      void refreshList()
    })
}

watch(
  () => [props.deviceId, props.dateRange] as const,
  () => {
    listForm.device_id = props.deviceId
    const r = rangeToStartEnd()
    listForm.start_time = r.start_time
    listForm.end_time = r.end_time
    listForm.page = 1
    void refreshList()
  },
  { deep: true }
)

return (_ctx: any,_cache: any) => {
  const _component_el_input = _resolveComponent("el-input")!
  const _component_el_radio = _resolveComponent("el-radio")!
  const _component_el_radio_group = _resolveComponent("el-radio-group")!
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_dialog = _resolveComponent("el-dialog")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(CmTable, {
      ref_key: "tableRef",
      ref: tableRef,
      modelValue: listForm,
      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((listForm) = $event)),
      class: "installed-detail-cmtable",
      "table-opt": tableOpt.value,
      "table-act-opt": tableActOpt.value,
      api: listApi,
      "first-loading": true,
      "page-option": pageOption,
      "has-permission": hasPermission.value,
      onHandleAction: handleTableAction
    }, {
      header: _withCtx(() => [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _createVNode(ExportDropdown, {
              "has-permission": hasPermission.value,
              onExport: download
            }, null, 8, ["has-permission"])
          ]),
          _createElementVNode("div", _hoisted_4, [
            _createVNode(_component_el_input, {
              modelValue: listForm.soft_name,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((listForm.soft_name) = $event)),
              size: "small",
              disabled: !hasPermission.value,
              placeholder: _ctx.$t('common.phSoftwareName'),
              "suffix-icon": "el-icon-search",
              onChange: onSoftNameChange
            }, null, 8, ["modelValue", "disabled", "placeholder"])
          ])
        ])
      ]),
      avg_duration: _withCtx(({ row }) => [
        _createTextVNode(_toDisplayString(_unref(formatDuration)(row.duration * 60) || '--'), 1)
      ]),
      avg_use_count: _withCtx(({ row }) => [
        _createTextVNode(_toDisplayString(row.use_soft_count), 1)
      ]),
      last_active_time: _withCtx(({ row }) => [
        _createTextVNode(_toDisplayString(formatLastActive(row)), 1)
      ]),
      install_time: _withCtx(({ row }) => [
        _createTextVNode(_toDisplayString(_unref(formatTime)(row.install_time) || '--'), 1)
      ]),
      _: 1
    }, 8, ["modelValue", "table-opt", "table-act-opt", "page-option", "has-permission"]),
    _createVNode(_component_el_dialog, {
      modelValue: uninstallDialogVisible.value,
      "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((uninstallDialogVisible).value = $event)),
      title: _ctx.$t('genuineSoftwareUsage.uninstallDialogTitle'),
      width: "30%",
      "destroy-on-close": "",
      onClosed: resetUninstallDialog
    }, {
      footer: _withCtx(() => [
        _createVNode(_component_el_button, {
          size: "small",
          type: "primary",
          onClick: confirmUninstall
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_ctx.$t('common.confirm')), 1)
          ]),
          _: 1
        })
      ]),
      default: _withCtx(() => [
        _createVNode(_component_el_radio_group, {
          modelValue: uninstallType.value,
          "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((uninstallType).value = $event))
        }, {
          default: _withCtx(() => [
            _createVNode(_component_el_radio, { label: 0 }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.$t('genuineSoftwareUsage.uninstallTypeNotify')), 1)
              ]),
              _: 1
            }),
            _createVNode(_component_el_radio, { label: 1 }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.$t('genuineSoftwareUsage.uninstallTypeSilent')), 1)
              ]),
              _: 1
            })
          ]),
          _: 1
        }, 8, ["modelValue"])
      ]),
      _: 1
    }, 8, ["modelValue", "title"])
  ]))
}
}

})